import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Heart, MessageCircle, Share2, Bookmark, Eye, Play, Clock, BarChart3,
  TrendingUp, ExternalLink, AlertTriangle, RefreshCw, UserPlus, MousePointerClick,
  Trash2,
} from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import {
  instagramService,
  IgMediaItem,
  IgMediaInsightsResponse,
} from '@/services/instagram.service';

interface Props {
  open: boolean;
  onClose: () => void;
  media: IgMediaItem | null;
  /** Called after a successful delete so the parent page can refetch. */
  onDeleted?: (mediaId: string) => void;
}

interface MetricMeta {
  icon: React.ReactNode;
  label: string;
  /** When true, render the value as seconds-formatted (mm:ss). */
  isDuration?: boolean;
  /** Tone hint for the tile color. */
  tone?: 'default' | 'success' | 'info' | 'warning';
}

const METRIC_META: Record<string, MetricMeta> = {
  reach:                            { icon: <Eye size={14} />,             label: 'Reach',              tone: 'info' },
  impressions:                      { icon: <BarChart3 size={14} />,       label: 'Impressions',        tone: 'info' },
  saved:                            { icon: <Bookmark size={14} />,        label: 'Saves',              tone: 'success' },
  likes:                            { icon: <Heart size={14} />,           label: 'Likes' },
  comments:                         { icon: <MessageCircle size={14} />,   label: 'Comments' },
  shares:                           { icon: <Share2 size={14} />,          label: 'Shares' },
  total_interactions:               { icon: <TrendingUp size={14} />,      label: 'Total interactions', tone: 'success' },
  plays:                            { icon: <Play size={14} />,            label: 'Plays' },
  clips_replays_count:              { icon: <Play size={14} />,            label: 'Replays' },
  ig_reels_avg_watch_time:          { icon: <Clock size={14} />,           label: 'Avg watch time',     isDuration: true },
  ig_reels_video_view_total_time:   { icon: <Clock size={14} />,           label: 'Total watch time',   isDuration: true },
  ig_reels_aggregated_all_plays_count: { icon: <Play size={14} />,         label: 'All plays' },
  profile_visits:                   { icon: <Eye size={14} />,             label: 'Profile visits' },
  profile_activity:                 { icon: <MousePointerClick size={14} />, label: 'Profile actions' },
  follows:                          { icon: <UserPlus size={14} />,        label: 'Follows from post',  tone: 'success' },
  navigation:                       { icon: <MousePointerClick size={14} />, label: 'Navigations' },
};

function formatDuration(ms: number): string {
  // IG returns durations in milliseconds for reels — convert to mm:ss.
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export const InstagramPostInsightsModal: React.FC<Props> = ({ open, onClose, media, onDeleted }) => {
  const [data, setData] = useState<IgMediaInsightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async (mediaId: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await instagramService.mediaInsights(mediaId);
      setData(r);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load insights');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open || !media?.id) return;
    setData(null);
    load(media.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, media?.id]);

  const remove = async () => {
    if (!media?.id) return;
    if (!window.confirm('Permanently delete this Instagram post? This cannot be undone — Meta does not provide a restore.')) return;
    setDeleting(true);
    try {
      await instagramService.deleteMedia(media.id);
      toast.success('Post deleted');
      onDeleted?.(media.id);
      onClose();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setDeleting(false); }
  };

  if (!media) return null;
  // Prefer ImageKit mirror — IG's signed CDN URLs expire in a few hours.
  const thumb = media.imagekit_url || media.thumbnail_url || media.media_url;
  const dateStr = media.timestamp ? new Date(media.timestamp).toLocaleString() : '—';

  return (
    <Modal open={open} onClose={onClose} title="Post insights" widthClass="max-w-3xl">
      <div className="space-y-4">
        {/* Header — thumbnail + meta */}
        <div className="flex gap-3 rounded-xl border border-border bg-surface p-3">
          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-surface-alt">
            {thumb && <img src={thumb} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="rounded-full bg-info-soft px-2 py-0.5 text-2xs font-semibold text-info">
                {media.media_type}{media.media_product_type ? ` · ${media.media_product_type}` : ''}
              </span>
              <span className="text-2xs text-text-muted">{dateStr}</span>
            </div>
            {media.caption && (
              <p className="line-clamp-3 text-xs text-text-secondary leading-relaxed">{media.caption}</p>
            )}
            {media.permalink && (
              <a
                href={media.permalink}
                target="_blank"
                rel="noreferrer"
                className="mt-1.5 inline-flex items-center gap-1 text-2xs text-primary hover:underline"
              >
                Open on Instagram <ExternalLink size={10} />
              </a>
            )}
          </div>
        </div>

        {/* Engagement strip — fields always available from the list endpoint. */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Tile
            icon={<Heart size={14} />}
            label="Likes"
            value={(media.like_count ?? 0).toLocaleString()}
          />
          <Tile
            icon={<MessageCircle size={14} />}
            label="Comments"
            value={(media.comments_count ?? 0).toLocaleString()}
          />
          {data && data.metrics.find((m) => m.name === 'reach') && (
            <Tile
              icon={<Eye size={14} />}
              label="Reach"
              value={(data.metrics.find((m) => m.name === 'reach')?.value ?? 0).toLocaleString()}
              tone="info"
            />
          )}
          {data && data.metrics.find((m) => m.name === 'saved') && (
            <Tile
              icon={<Bookmark size={14} />}
              label="Saves"
              value={(data.metrics.find((m) => m.name === 'saved')?.value ?? 0).toLocaleString()}
              tone="success"
            />
          )}
        </div>

        {/* Full metric list */}
        <div className="rounded-xl border border-border bg-surface">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <div className="flex items-center gap-2">
              <BarChart3 size={13} className="text-primary" />
              <span className="text-xs font-semibold text-text-primary">All metrics from Meta</span>
            </div>
            {media.id && (
              <button
                type="button"
                onClick={() => media.id && load(media.id)}
                disabled={loading}
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-2 py-0.5 text-2xs text-text-secondary hover:bg-surface-alt disabled:opacity-50"
              >
                <RefreshCw size={10} className={clsx(loading && 'animate-spin')} /> Refresh
              </button>
            )}
          </div>

          <div className="p-3">
            {loading && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton h-16 rounded-lg" />)}
              </div>
            )}

            {!loading && error && (
              <div className="flex items-start gap-2 rounded-lg border border-danger/40 bg-danger-soft p-3 text-xs text-danger">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">Couldn't load insights</div>
                  <div className="mt-0.5 text-text-secondary">{error}</div>
                </div>
              </div>
            )}

            {!loading && !error && data && data.metrics.length === 0 && (
              <div className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-4 py-6 text-center text-xs text-text-muted">
                No insights available yet — Instagram needs ~24h after posting before metrics populate.
              </div>
            )}

            {!loading && !error && data && data.metrics.length > 0 && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {data.metrics.map((m) => {
                  const meta = METRIC_META[m.name];
                  const display = meta?.isDuration ? formatDuration(m.value) : m.value.toLocaleString();
                  return (
                    <Tile
                      key={m.name}
                      icon={meta?.icon ?? <BarChart3 size={14} />}
                      label={meta?.label ?? m.title ?? m.name}
                      value={display}
                      tone={meta?.tone}
                      title={m.description}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border px-1 pt-4 mt-4">
        <span className="text-2xs text-text-muted">
          {data ? `Pulled from Meta at ${new Date(data.fetchedAt).toLocaleTimeString()}` : ' '}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={remove}
            loading={deleting}
            iconLeft={<Trash2 size={13} />}
            className="text-danger hover:bg-danger-soft"
          >
            Delete post
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={deleting}>Close</Button>
        </div>
      </div>
    </Modal>
  );
};

const Tile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'info' | 'warning';
  title?: string;
}> = ({ icon, label, value, tone, title }) => (
  <div
    title={title}
    className="rounded-xl border border-border bg-surface-alt/40 p-2.5"
  >
    <div className={clsx(
      'flex items-center gap-1.5 mb-0.5 text-2xs uppercase tracking-wide',
      tone === 'success' ? 'text-success'
      : tone === 'info' ? 'text-info'
      : tone === 'warning' ? 'text-warning'
      : 'text-text-muted',
    )}>
      <span>{icon}</span>
      <span className="truncate">{label}</span>
    </div>
    <div className="font-display text-lg font-bold text-text-primary tabular-nums">{value}</div>
  </div>
);
